import PaidRoundedIcon from '@mui/icons-material/PaidRounded'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { displayNamesOfEventTypes } from 'src/consumer/constants'
import { markSupportBookedEventAsPaid } from 'src/supportPortal/services/supportBookedEventActionsService'

dayjs.extend(utc)

const GATEWAY_OPTIONS = [
  { value: 'cash', label: 'Cash at clinic' },
  { value: 'upi', label: 'UPI (direct)' },
  { value: 'card', label: 'Card (offline machine)' },
  { value: 'bank-transfer', label: 'Bank transfer' },
  { value: 'other', label: 'Other' },
]

export default function SupportMarkAsPaidDialog({ open, onClose, bookedEvent, onSuccess }) {
  const [reasonGiven, setReasonGiven] = useState('')
  const [paymentGatewayUsed, setPaymentGatewayUsed] = useState('cash')
  const [paymentTxnId, setPaymentTxnId] = useState('')
  const [feeCollectionNote, setFeeCollectionNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [submitError, setSubmitError] = useState('')

  useEffect(() => {
    if (!open) {
      return
    }
    setReasonGiven('')
    setPaymentGatewayUsed('cash')
    setPaymentTxnId('')
    setFeeCollectionNote('')
    setSubmitError('')
  }, [open, bookedEvent])

  const patientName = bookedEvent?.patientInfoData?.name || bookedEvent?.userName || 'Patient'
  const eventTypeLabel =
    displayNamesOfEventTypes?.[bookedEvent?.eventType] || bookedEvent?.eventType || 'Session'
  const feeLabel = bookedEvent?.feePaid
    ? `${bookedEvent?.currency || 'INR'} ${bookedEvent.feePaid}`
    : null

  async function handleSubmit() {
    if (!reasonGiven.trim()) {
      toast.error('Please describe how/why this payment is confirmed')
      return
    }
    setSaving(true)
    setSubmitError('')
    const result = await markSupportBookedEventAsPaid({
      bookedEventId: bookedEvent?.id,
      reasonGiven: reasonGiven.trim(),
      paymentGatewayUsed,
      paymentTxnId: paymentTxnId.trim() || undefined,
      feeCollectionNote: feeCollectionNote.trim() || undefined,
    })
    setSaving(false)

    if (result.ok) {
      toast.success('Booking marked as paid')
      onSuccess?.()
      onClose?.()
      return
    }
    setSubmitError(result.message)
    toast.error(result.message)
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <PaidRoundedIcon sx={{ color: '#047857' }} />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 17, color: '#0f172a' }}>
              Mark as Paid
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: '#64748b' }}>
              {patientName} · {eventTypeLabel}
              {bookedEvent?.startTime
                ? ` · ${dayjs.utc(bookedEvent.startTime).local().format('DD MMM, h:mm A')}`
                : ''}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.75} sx={{ mt: 0.5 }}>
          {submitError ? (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {submitError}
            </Alert>
          ) : null}
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            This confirms {feeLabel ? <strong>{feeLabel}</strong> : 'the fee'} was collected outside
            the payment gateway and books the session. Existing payment links are handled
            automatically. The confirmation is recorded against your account.
          </Alert>
          <TextField
            label="Reason / how the payment was received *"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={reasonGiven}
            onChange={(event) => setReasonGiven(event.target.value)}
            placeholder="e.g. Patient paid in cash at the front desk"
          />
          <TextField
            select
            label="Payment mode"
            size="small"
            fullWidth
            value={paymentGatewayUsed}
            onChange={(event) => setPaymentGatewayUsed(event.target.value)}
          >
            {GATEWAY_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Transaction reference (optional)"
            size="small"
            fullWidth
            value={paymentTxnId}
            onChange={(event) => setPaymentTxnId(event.target.value)}
          />
          <TextField
            label="Fee collection note (optional)"
            size="small"
            fullWidth
            value={feeCollectionNote}
            onChange={(event) => setFeeCollectionNote(event.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit}
          disabled={saving || !reasonGiven.trim()}
        >
          {saving ? 'Confirming...' : 'Confirm Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
